import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLContainer } from '../smlContainer/SMLContainer';
import { ErrorAlert } from '../../common/views/ErrorAlert/ErrorAlert';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useOpenReleasePage } from '../../music24/promote/releases/useOpenReleasePage';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { catalogSongPath } from './song/page/catalogSongPath';
import { catalogAddSongPath } from './addSong/catalogAddSongPath';
import { songPageMessages } from './song/page/songPageMessages';
import { useOpenSongPage } from './song/page/useOpenSongPage';
import {
  useDismissCatalogSuggestionMutation,
  useGetAccountQuery,
  useGetCatalogQuery,
  useGetCatalogSuggestionsQuery,
} from '../../../redux/api/fymAPI';
import { useIsDemoAccount } from '../../accountSelector/useIsDemoAccount';
import { canOfferMerge } from './merge/mergeRules';
import { CatalogItem, CatalogReleaseEvent } from '../../../redux/api/fymAPI.types';
import { SuggestionsPanel } from './SuggestionsPanel';
import { CatalogTablePanel } from './CatalogTablePanel';
import { CatalogPageHead } from './CatalogPageHead';
import { CatalogConnectionsPanel } from './CatalogConnectionsPanel';
import { CatalogConnectionModals } from './CatalogConnectionModals';
import { CatalogAddModals } from './CatalogAddModals';
import { CatalogRowModals } from './CatalogRowModals';
import { useCatalogConnections } from './useCatalogConnections';
import { useCatalogAddModals } from './useCatalogAddModals';
import { useMergeSuggestion } from './claimWay/useMergeSuggestion';
import { useCatalogRowModals } from './useCatalogRowModals';
import { useCatalogList } from './useCatalogList';
import { useCatalogItemActions } from './useCatalogItemActions';
import { CatalogRowMenuActions } from './CatalogRowMenu';
import { ReleaseEventCards } from './release/ReleaseEventCards';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
import { trackEvent } from '../../analytics/sml/analytics';
/* eslint-enable css-modules/no-unused-class */

interface CatalogScreenProps {
  accountId: number;
  /** The service of a `/catalog/connect/:service` link (design your-intelligence h4). */
  connectService?: string;
}

/**
 * Promote / Catalog — everything Music24 will let this account promote.
 *
 * Two independent axes live here. Connection state belongs to a service and
 * ownership belongs to a release, and neither blocks the other: an artist who
 * connects nothing still has a complete catalog and a complete creation flow.
 */
const CatalogScreenComponent: React.FC<CatalogScreenProps> = ({ accountId, connectService }) => {
  const router = useSMLRouter();

  const { data: accountResponse } = useGetAccountQuery({ accountId });
  const artistName = accountResponse?.account.name ?? '';

  const catalog = useCatalogList({ accountId });
  const { data: suggestionsResponse } = useGetCatalogSuggestionsQuery({ accountId });
  const [dismissSuggestion] = useDismissCatalogSuggestionMutation();
  const connections = useCatalogConnections({ accountId, isCatalogLoading: catalog.isLoading, connectService });
  const addModals = useCatalogAddModals(suggestionsResponse?.suggestions);
  // design catalog-merge c1: "Merge" and "Not the same" on a suggestion that matches a song.
  const suggestionMerge = useMergeSuggestion({ accountId, openDialog: addModals.onClaimSuggestion });
  const navigate = useNavigate();
  // design catalog-add-song: "Add a song" is a page with one question per step, not a modal.
  const openAddSong = useCallback(() => {
    // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
    void navigate(catalogAddSongPath({ from: 'catalog', accountId }));
  }, [accountId, navigate]);
  const itemActions = useCatalogItemActions({ accountId });

  const rowModals = useCatalogRowModals();
  // design catalog-merge a1: the whole catalog counts, not the filtered list.
  const { songCount } = useGetCatalogQuery(
    { accountId },
    { selectFromResult: ({ data }) => ({ songCount: data?.items.length ?? 0 }) }
  );
  const isDemo = useIsDemoAccount();
  const onMerge = canOfferMerge(songCount, isDemo) ? rowModals.openMerge : undefined;
  const intl = useIntl();
  const { showSuccess } = useShowToast();
  // The whole row opens the song page (design song-page a1); in the native apps, the native page.
  const openSongPage = useOpenSongPage();
  const onOpenSong = useCallback<CatalogRowMenuActions['onOpenSong']>(
    (item, section, view) => openSongPage(item.uuid, section, view),
    [openSongPage]
  );

  const onDismissSuggestion = useCallback(
    (catalogTrackMaid: string) => {
      dismissSuggestion({ accountId, catalogTrackMaid }).catch(console.error);
    },
    [accountId, dismissSuggestion]
  );

  const onNotTheSame = useCallback(
    (catalogTrackMaid: string) => {
      dismissSuggestion({ accountId, catalogTrackMaid, notTheSame: true }).catch(console.error);
    },
    [accountId, dismissSuggestion]
  );

  const onOpenSmartLink = useCallback(
    (smartLinkId: number) => {
      router.push(SMLRoutes.manage, { accountId, smartLinkId });
    },
    [accountId, router]
  );

  // design g3: "See the release" opens the song page of the row that is now the released track.
  const onSeeRelease = useCallback((event: CatalogReleaseEvent) => openSongPage(event.catalogItemUuid), [openSongPage]);

  const onMakeInStudio = useCallback(
    (item: CatalogItem) => router.push(SMLRoutes.promoteStudio, { catalogItemUuid: item.uuid }),
    [router]
  );

  // "Open in Releases" of the row menu: the song page of the row in Releases; in the native apps,
  // their own song page.
  const openReleasePage = useOpenReleasePage({ accountId });
  const onOpenRelease = useCallback((item: CatalogItem) => openReleasePage(item.uuid), [openReleasePage]);

  const onCopyLink = useCallback(
    (item: CatalogItem) => {
      const link = `${window.location.origin}/app${catalogSongPath(item.uuid, { accountId })}`;
      navigator.clipboard
        ?.writeText(link)
        .then(() => {
          trackEvent('link_copied', { kind: 'track' });
          showSuccess(intl.formatMessage(songPageMessages.menuLinkCopied));
        })
        .catch(console.error);
    },
    [accountId, intl, showSuccess]
  );

  return (
    <SMLContainer header={<CatalogPageHead onAddSong={openAddSong} />}>
      <div className={styles.panelStack}>
        {itemActions.error !== undefined && <ErrorAlert error={itemActions.error} />}

        <ReleaseEventCards accountId={accountId} onSeeRelease={onSeeRelease} />

        <SuggestionsPanel
          suggestions={suggestionsResponse?.suggestions}
          onDismiss={onDismissSuggestion}
          onClaim={addModals.onClaimSuggestion}
          onMerge={suggestionMerge.merge}
          onNotTheSame={onNotTheSame}
          busyMaid={suggestionMerge.busyMaid}
        />

        <CatalogTablePanel
          accountId={accountId}
          items={catalog.items}
          isLoading={catalog.isLoading}
          error={catalog.error}
          retry={catalog.retry}
          search={catalog.search}
          onSearchChange={catalog.setSearch}
          filter={catalog.filter}
          onFilterChange={catalog.setFilter}
          onOpenSmartLink={onOpenSmartLink}
          onCreateLink={rowModals.openCreate}
          onOpenSong={onOpenSong}
          onMakeInStudio={onMakeInStudio}
          onOpenRelease={onOpenRelease}
          onCopyLink={onCopyLink}
          onRemove={rowModals.openRemove}
          onMerge={onMerge}
        />

        {/* The owner put the catalog first (2026-10-03). Connections matter while an artist
            sets them up, and after that they only need to be within reach. */}
        <div ref={connections.connectionsRef}>
          <CatalogConnectionsPanel connections={connections} />
        </div>
      </div>

      <CatalogAddModals accountId={accountId} artistName={artistName} modals={addModals} onAddSong={openAddSong} />
      <CatalogRowModals accountId={accountId} modals={rowModals} itemActions={itemActions} />
      <CatalogConnectionModals accountId={accountId} connections={connections} />
    </SMLContainer>
  );
};

export const CatalogScreen = TypedMemo(CatalogScreenComponent);
